import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sun, ArrowRight, Check, Building, User, FolderPlus } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = ["Company Profile", "Create First Project", "Invite Team (Optional)"];

export default function OnboardingPage() {
  const { user, workspace, refreshWorkspace } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const [wsForm, setWsForm] = useState({
    address: workspace?.address || "",
    state: workspace?.state || "",
    phone: workspace?.phone || "",
    gstin: workspace?.gstin || "",
    authorized_signatory: workspace?.authorized_signatory || "",
    bank_name: "", bank_account: "", bank_ifsc: "", bank_branch: "",
  });

  const [projForm, setProjForm] = useState({
    name: "", client_name: "", location: "", capacity_kwp: "", contract_value: "", expected_cod: "", client_email: ""
  });

  const [inviteForm, setInviteForm] = useState({ email: "", full_name: "", role: "project_manager" });

  const saveWorkspace = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/workspaces/current`, wsForm);
      await refreshWorkspace();
      toast.success("Company profile saved!");
      setStep(1);
    } catch (err) {
      toast.error("Failed to save. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const createProject = async () => {
    if (!projForm.name || !projForm.client_name || !projForm.location || !projForm.capacity_kwp) {
      toast.error("Please fill required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/projects`, {
        ...projForm,
        capacity_kwp: parseFloat(projForm.capacity_kwp),
        contract_value: parseFloat(projForm.contract_value) || 0,
      });
      toast.success("Project created with its task plan auto-generated!");
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create project");
    } finally {
      setLoading(false);
    }
  };

  const inviteUser = async () => {
    if (inviteForm.email && inviteForm.full_name) {
      try {
        const { data } = await axios.post(`${API}/users/invite`, inviteForm);
        toast.success(`Invited ${inviteForm.full_name}. Temp password: ${data.temp_password}`);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to invite user");
      }
    }
    navigate("/app/dashboard");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-body">
      {/* Header */}
      <div className="bg-[#F5F8F5] py-4 px-6 flex items-center gap-3">
        <div className="w-8 h-8 bg-[#FFB703] rounded flex items-center justify-center">
          <Sun size={16} className="text-[#0A1128]" />
        </div>
        <span className="font-heading font-bold text-[#1B2B22]">SunBiz</span>
        <span className="text-[#64748B] text-sm ml-2">— Setup Wizard</span>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-10">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 ${i <= step ? "text-[#0A1128]" : "text-[#94A3B8]"}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                  i < step ? "bg-[#059669] border-[#059669] text-[#1B2B22]" :
                  i === step ? "border-[#FFB703] text-[#FFB703]" :
                  "border-[#E2E8F0] text-[#94A3B8]"
                }`}>
                  {i < step ? <Check size={12} /> : i + 1}
                </div>
                <span className="text-xs font-medium hidden sm:inline">{s}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-0.5 ${i < step ? "bg-[#059669]" : "bg-[#E2E8F0]"}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 0: Company Profile */}
        {step === 0 && (
          <div className="bg-white border border-[#E2E8F0] rounded p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#FEF3C7] rounded flex items-center justify-center">
                <Building size={18} className="text-[#FFB703]" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl text-[#0A1128]">Company Profile</h2>
                <p className="text-sm text-[#64748B]">This information appears on your PO/PI documents.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Registered Address</label>
                <textarea
                  value={wsForm.address}
                  onChange={e => setWsForm(p => ({ ...p, address: e.target.value }))}
                  rows={2}
                  placeholder="123 Solar Street, Ahmedabad, Gujarat"
                  className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">State</label>
                <input type="text" value={wsForm.state} onChange={e => setWsForm(p => ({ ...p, state: e.target.value }))} placeholder="Gujarat" className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">GSTIN</label>
                <input type="text" value={wsForm.gstin} onChange={e => setWsForm(p => ({ ...p, gstin: e.target.value }))} placeholder="22AAAAA0000A1Z5" className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Authorized Signatory Name</label>
                <input type="text" value={wsForm.authorized_signatory} onChange={e => setWsForm(p => ({ ...p, authorized_signatory: e.target.value }))} placeholder="Rajesh Kumar" className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div className="col-span-2 border-t border-[#E2E8F0] pt-4">
                <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-3">Bank Details (for PI documents)</p>
              </div>
              <div>
                <label className="block text-xs text-[#64748B] mb-1">Bank Name</label>
                <input type="text" value={wsForm.bank_name} onChange={e => setWsForm(p => ({ ...p, bank_name: e.target.value }))} placeholder="HDFC Bank" className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div>
                <label className="block text-xs text-[#64748B] mb-1">Account Number</label>
                <input type="text" value={wsForm.bank_account} onChange={e => setWsForm(p => ({ ...p, bank_account: e.target.value }))} placeholder="123456789012" className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div>
                <label className="block text-xs text-[#64748B] mb-1">IFSC Code</label>
                <input type="text" value={wsForm.bank_ifsc} onChange={e => setWsForm(p => ({ ...p, bank_ifsc: e.target.value }))} placeholder="HDFC0001234" className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div>
                <label className="block text-xs text-[#64748B] mb-1">Branch</label>
                <input type="text" value={wsForm.bank_branch} onChange={e => setWsForm(p => ({ ...p, bank_branch: e.target.value }))} placeholder="Ahmedabad Main" className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
            </div>
            <div className="flex justify-between mt-6">
              <button onClick={() => navigate("/app/dashboard")} className="text-sm text-[#64748B] hover:text-[#0A1128]">Skip for now</button>
              <button onClick={saveWorkspace} disabled={loading} className="bg-[#FFB703] text-[#0A1128] font-semibold px-6 py-2.5 rounded text-sm hover:bg-[#E8A200] transition-colors active:scale-[0.98] flex items-center gap-2 disabled:opacity-60" data-testid="save-workspace-btn">
                {loading ? "Saving..." : (<><span>Save & Continue</span><ArrowRight size={14} /></>)}
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Create First Project */}
        {step === 1 && (
          <div className="bg-white border border-[#E2E8F0] rounded p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#FEF3C7] rounded flex items-center justify-center">
                <FolderPlus size={18} className="text-[#FFB703]" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl text-[#0A1128]">Create Your First Project</h2>
                <p className="text-sm text-[#64748B]">63 tasks (rooftop), 66 (ground-mount) or 87 (open access) will be auto-generated across 8 phases.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Project Name *</label>
                <input type="text" required value={projForm.name} onChange={e => setProjForm(p => ({ ...p, name: e.target.value }))} placeholder="Acme Foods - 500kWp Rooftop Solar" className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" data-testid="project-name-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Client Name *</label>
                <input type="text" required value={projForm.client_name} onChange={e => setProjForm(p => ({ ...p, client_name: e.target.value }))} placeholder="Acme Foods Pvt Ltd" className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Client Email</label>
                <input type="email" value={projForm.client_email} onChange={e => setProjForm(p => ({ ...p, client_email: e.target.value }))} placeholder="ceo@acmefoods.com" className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Location / Site *</label>
                <input type="text" required value={projForm.location} onChange={e => setProjForm(p => ({ ...p, location: e.target.value }))} placeholder="Ahmedabad, Gujarat" className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Capacity (kWp) *</label>
                <input type="number" required value={projForm.capacity_kwp} onChange={e => setProjForm(p => ({ ...p, capacity_kwp: e.target.value }))} placeholder="500" className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Contract Value (₹)</label>
                <input type="number" value={projForm.contract_value} onChange={e => setProjForm(p => ({ ...p, contract_value: e.target.value }))} placeholder="25000000" className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Expected COD</label>
                <input type="date" value={projForm.expected_cod} onChange={e => setProjForm(p => ({ ...p, expected_cod: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
            </div>
            <div className="flex justify-between mt-6">
              <button onClick={() => setStep(2)} className="text-sm text-[#64748B] hover:text-[#0A1128]">Skip for now</button>
              <button onClick={createProject} disabled={loading} className="bg-[#FFB703] text-[#0A1128] font-semibold px-6 py-2.5 rounded text-sm hover:bg-[#E8A200] transition-colors active:scale-[0.98] flex items-center gap-2 disabled:opacity-60" data-testid="create-project-btn">
                {loading ? "Creating..." : (<><span>Create Project</span><ArrowRight size={14} /></>)}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Invite Team */}
        {step === 2 && (
          <div className="bg-white border border-[#E2E8F0] rounded p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#FEF3C7] rounded flex items-center justify-center">
                <User size={18} className="text-[#FFB703]" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl text-[#0A1128]">Invite Your First Team Member</h2>
                <p className="text-sm text-[#64748B]">You can invite more members later from the Team section.</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Full Name</label>
                <input type="text" value={inviteForm.full_name} onChange={e => setInviteForm(p => ({ ...p, full_name: e.target.value }))} placeholder="Priya Sharma" className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Email Address</label>
                <input type="email" value={inviteForm.email} onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))} placeholder="priya@company.com" className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Role</label>
                <select value={inviteForm.role} onChange={e => setInviteForm(p => ({ ...p, role: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white">
                  <option value="project_manager">Project Manager</option>
                  <option value="field_engineer">Field Engineer</option>
                  <option value="management_view">Management View</option>
                  <option value="client">Client (Read-Only)</option>
                </select>
              </div>
            </div>
            <div className="flex justify-between mt-6">
              <button onClick={() => navigate("/app/dashboard")} className="text-sm text-[#64748B] hover:text-[#0A1128]">Skip for now</button>
              <button onClick={inviteUser} className="bg-[#FFB703] text-[#0A1128] font-semibold px-6 py-2.5 rounded text-sm hover:bg-[#E8A200] transition-colors active:scale-[0.98] flex items-center gap-2" data-testid="invite-user-btn">
                <span>Invite & Go to Dashboard</span>
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
